import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, Loader2 } from 'lucide-react';

import { mfaApi } from '@/features/auth/mfa.api';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { ApiError } from '@/lib/api-client';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { Kicker, TintBox, BTN_PRI, BTN_SEC, FIELD_LABEL } from '@/components/brand/brand';
import { Skeleton } from '@/components/chassis/States';

/**
 * F5-T7 · Inscripción MFA (contrato Agente-05, ventana 9) — el mismo chasis
 * del login: un solo primario, QR de 112px en marco blanco con el secreto en
 * TEXTO como alternativa real (grupos de cuatro separados por «·»), y el
 * código de confirmación con su pista.
 *
 * Lo que el contrato promete y NECESITA SERVER (no se finge acá): los cinco
 * códigos de respaldo de un solo uso tras confirmar — el backend no los emite
 * todavía, así que el aviso teal que los anuncia no se dibuja (anunciar lo
 * que no existe es peor que no anunciarlo).
 *
 * Lo vivo se conserva: enroll POST + verify + bounce si ya está inscripto +
 * copiar el secreto crudo.
 */
export function MfaEnrollPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [code, setCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  const enrollQ = useQuery({
    queryKey: ['mfa', 'enroll'],
    queryFn: mfaApi.enroll,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Infinity,
    enabled: !user?.mfaEnabled,
  });

  const verify = useMutation({
    mutationFn: (c: string) => mfaApi.verify(c),
    onSuccess: () => {
      const current = useAuthStore.getState().user;
      if (current) {
        useAuthStore.setState({ user: { ...current, mfaEnabled: true } });
      }
      toast.success('MFA', t('agentF5.mfa.codeHint'));
      navigate('/settings');
    },
  });

  // ¿Ya inscripto? Afuera.
  useEffect(() => {
    if (user?.mfaEnabled) navigate('/settings', { replace: true });
  }, [user?.mfaEnabled, navigate]);

  const copySecret = async (): Promise<void> => {
    if (!enrollQ.data?.secret) return;
    try {
      await navigator.clipboard.writeText(enrollQ.data.secret);
      setSecretCopied(true);
      window.setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      toast.warning(t('agentF5.mfa.copy'), enrollQ.data.secret);
    }
  };

  /** El secreto en grupos de cuatro separados por «·» — alternativa real al QR. */
  const groupedSecret = (enrollQ.data?.secret ?? '')
    .replace(/\s+/g, '')
    .match(/.{1,4}/g)
    ?.join(' · ');

  return (
    <div className="max-w-[560px] mx-auto font-body text-nav-navy animate-fade-up-slow">
      <div className="bg-white border-[1.5px] border-nav-card rounded-[14px] px-7 py-8 sm:px-10 sm:py-10">
        <Kicker className="mb-1.5">{t('agentF5.mfa.kicker')}</Kicker>
        <h1 className="font-head text-2xl font-extrabold tracking-[-0.02em] m-0 mb-1.5">
          {t('agentF5.mfa.title')}
        </h1>
        <p className="text-sm leading-[1.55] text-nav-slate m-0 mb-6">{t('agentF5.mfa.sub')}</p>

        {enrollQ.isLoading && (
          <div className="flex gap-4 items-start mb-5" aria-busy="true">
            <Skeleton className="w-[124px] h-[124px]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        )}

        {enrollQ.error && (
          <TintBox tone="red" className="mb-5">
            <p className="font-head text-[13px] font-bold m-0 mb-0.5">{t('agentF5.mfa.genFail')}</p>
            <p className="m-0 text-[13px]">
              {enrollQ.error instanceof ApiError
                ? enrollQ.error.message
                : (enrollQ.error as Error).message}
            </p>
          </TintBox>
        )}

        {enrollQ.data && (
          <div className="flex gap-4 flex-wrap bg-nav-softer border-[1.5px] border-nav-card rounded-xl p-[18px] mb-5">
            <span className="bg-white border-[1.5px] border-nav-card rounded-lg p-1.5 shrink-0 self-start">
              <img
                src={enrollQ.data.qrDataUrl}
                role="img"
                alt={t('agentF5.mfa.qrAria')}
                className="w-28 h-28 block"
              />
            </span>
            <div className="flex-1 min-w-[200px]">
              <p className="text-[13px] leading-[1.6] m-0">{t('agentF5.mfa.scanHint')}</p>
              <p className="text-[12.5px] leading-[1.6] text-nav-slate mt-1.5 m-0">
                {t('agentF5.mfa.manualHint')}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <p className="font-head text-sm font-extrabold tracking-[.06em] tabular-nums m-0 break-all">
                  {groupedSecret}
                </p>
                <button
                  type="button"
                  onClick={copySecret}
                  aria-label={t('agentF5.mfa.copy')}
                  title={t('agentF5.mfa.copy')}
                  className="w-11 h-11 shrink-0 rounded-lg flex items-center justify-center text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
                >
                  {secretCopied ? (
                    <Check className="h-4 w-4 text-nav-sageText" aria-hidden />
                  ) : (
                    <Copy className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {verify.error && (
          <TintBox tone="red" className="mb-4" >
            <p className="font-head text-[13px] font-bold m-0 mb-0.5">{t('agentF5.mfa.verifyFail')}</p>
            <p className="m-0 text-[13px]">
              {verify.error instanceof ApiError
                ? verify.error.message
                : (verify.error as Error).message}
            </p>
          </TintBox>
        )}

        <label className={FIELD_LABEL} htmlFor="mfa-code">
          {t('agentF5.mfa.codeLabel')}
        </label>
        <input
          id="mfa-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          maxLength={6}
          aria-describedby="mfa-enroll-hint"
          className="w-full box-border h-14 font-head text-[22px] font-bold tracking-[.22em] tabular-nums text-nav-navy bg-white border-[1.5px] border-nav-line rounded-lg px-4 focus:outline focus:outline-2 focus:outline-nav-teal focus:outline-offset-1 focus:border-nav-teal placeholder:text-nav-faint mt-0 mb-2"
        />
        <p id="mfa-enroll-hint" className="text-[12.5px] leading-[1.55] text-nav-slate m-0 mb-5">
          {t('agentF5.mfa.codeHint')}
        </p>

        <button
          type="button"
          onClick={() => verify.mutate(code)}
          disabled={code.length !== 6 || verify.isPending || !enrollQ.data}
          className={cn(BTN_PRI, 'w-full min-h-[52px] text-[15px]')}
        >
          {verify.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {verify.isPending ? t('agentF5.mfa.confirming') : t('agentF5.mfa.confirm')}
        </button>
        <div className="flex justify-center mt-3">
          <button type="button" className={cn(BTN_SEC, 'border-0')} onClick={() => navigate('/settings')}>
            {t('agentF5.mfa.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
